import React, { useEffect, useRef, useState } from 'react';
import '../styles/hero.css';

interface HeroProps {
  headline?: string[];
  tagline?: string;
  definitionTerm?: string;
  definitions?: string[];
  ctaButtons?: { text: string; href: string; primary?: boolean }[];
}

const Hero: React.FC<HeroProps> = ({
  headline = ["INSPIRED", "BY YOU.", "DRIVEN TO", "PROTECT YOU."],
  tagline = "Your story is our muse",
  definitionTerm = "muse",
  definitions = [
    "A person or personified force who is the source of inspiration.",
    "The term originates from the Muses, ancient Greek goddesses of inspiration in the arts and sciences."
  ],
  ctaButtons = [
    { text: "Contact us", href: "#contact", primary: true },
    { text: "How we work", href: "#about", primary: false }
  ]
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [contentScale, setContentScale] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    const scroller = scrollerRef.current;
    
    if (!container || !scroller) return;

    let accumulatedDelta = 0;
    const maxScroll = window.innerWidth * 0.75; // Reduced to properly center MUSE
    const pauseBuffer = 300; // Amount of wheel delta needed for pause/buffer zone
    const totalScroll = maxScroll + pauseBuffer; // Total including buffer

    const handleWheel = (e: WheelEvent) => {
      const rect = container.getBoundingClientRect();
      const heroAtTop = rect.top >= -10 && rect.top <= 0 && rect.bottom > 100;
      
      // Scrolling down/forward
      if (e.deltaY > 0 && accumulatedDelta < totalScroll && heroAtTop) {
        e.preventDefault();
        accumulatedDelta += Math.abs(e.deltaY);
        accumulatedDelta = Math.min(accumulatedDelta, totalScroll);
        
        // Determine which zone we're in
        if (accumulatedDelta <= maxScroll) {
          // Active horizontal scroll zone
          const clampedProgress = accumulatedDelta / maxScroll;
          setScrollProgress(clampedProgress);
          setContentScale(1);
          
          const translateX = -clampedProgress * maxScroll;
          scroller.style.transform = `translateX(${translateX}px)`;
        } else {
          // End pause/buffer zone - scale down content
          const bufferProgress = (accumulatedDelta - maxScroll) / pauseBuffer;
          const scale = 1.0 - (bufferProgress * 0.10); // Scale from 1.0 to 0.90
          setScrollProgress(1);
          setContentScale(scale);
          
          const translateX = -maxScroll;
          scroller.style.transform = `translateX(${translateX}px)`;
        }
      } 
      // Scrolling up/back
      else if (e.deltaY < 0 && accumulatedDelta > 0 && heroAtTop) {
        e.preventDefault();
        accumulatedDelta -= Math.abs(e.deltaY);
        accumulatedDelta = Math.max(accumulatedDelta, 0);
        
        // Determine which zone we're in
        if (accumulatedDelta >= maxScroll) {
          // End pause/buffer zone - scale up content as we go back
          const bufferProgress = (accumulatedDelta - maxScroll) / pauseBuffer;
          const scale = 1.0 - (bufferProgress * 0.10); // Scale from 0.90 back to 1.0
          setScrollProgress(1);
          setContentScale(scale);
          
          const translateX = -maxScroll;
          scroller.style.transform = `translateX(${translateX}px)`;
        } else {
          // Active horizontal scroll zone
          const clampedProgress = accumulatedDelta / maxScroll;
          setScrollProgress(clampedProgress);
          setContentScale(1);
          
          const translateX = -clampedProgress * maxScroll;
          scroller.style.transform = `translateX(${translateX}px)`;
        }
      }
      // Reset if scrolled completely past hero
      else if (rect.bottom < 0) {
        accumulatedDelta = totalScroll;
        setScrollProgress(1);
        setContentScale(0.90);
      }
      // Reset if scrolled back above hero
      else if (rect.top > window.innerHeight) {
        accumulatedDelta = 0;
        setScrollProgress(0);
        setContentScale(1);
        scroller.style.transform = 'translateX(0px)';
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="hero-wrapper"
      style={{
        minHeight: '100vh' // Lock to viewport until horizontal completes
      }}
    >
      <div className="hero-sticky">
        <div ref={scrollerRef} className="hero-scroller">
          {/* One continuous wide panel */}
          <div className="hero-panel">
            {/* Content on the left */}
            <div 
              className="hero-content-left"
              style={{
                transform: `scale(${contentScale})`,
                transition: 'transform 0.1s ease-out'
              }}
            >
              {/* Main headline */}
              <h1 className="hero-headline">
                {headline.map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < headline.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </h1>

              {/* Tagline positioned to the right of headline */}
              <div className="hero-tagline-wrapper">
                <p className="hero-tagline">
                  Your story<br />is our muse
                </p>
              </div>

              {/* Definition box */}
              <div className="hero-definition">
                <div className="definition-term">
                  <span className="term-text">{definitionTerm}</span>
                  <span className="term-pronunciation">/myooz/</span>
                  <span className="term-part-of-speech">[noun]</span>
                </div>
                <div className="definition-list">
                  {definitions.map((def, index) => (
                    <div key={index} className="definition-item">
                      <span className="definition-number">{index + 1}.</span>
                      <span className="definition-text">{def}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA buttons */}
              <div className="hero-cta-buttons">
                {ctaButtons.map((button, index) => (
                  <a
                    key={index}
                    href={button.href}
                    className={`hero-cta-button ${button.primary ? 'primary' : 'secondary'}`}
                  >
                    {button.text}
                  </a>
                ))}
              </div>
            </div>

            {/* Full MUSE word on the right - initially M is cut off */}
            <div 
              className="hero-muse-letters"
              style={{
                transform: `translateY(-50%) scale(${contentScale})`,
                transition: 'transform 0.1s ease-out'
              }}
            >
              <span className="muse-letter">M</span>
              <span className="muse-letter">U</span>
              <span className="muse-letter">S</span>
              <span className="muse-letter">E</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        {scrollProgress < 0.1 && (
          <div className="hero-scroll-indicator">
            <span className="scroll-text">SCROLL TO EXPLORE</span>
            <div className="scroll-arrow">↓</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Hero;

